import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  ApiResponse,
  EncroachmentClientConfig,
  GroupId,
  Summary,
} from './types';
import type {
  GroupAnalysisData,
  GroupAnalysisSpanData,
  GroupAnalysisType,
  GroupSummary,
} from '@/generated/types/navigator/types.gen';
import type { Feature, Geometry } from 'geojson';
import type { FilterState } from './filterSlice';
import { getFilterQuery, getFilterGroupIdParam } from './filterHelpers';
import { baseQueryWithAuth } from './utils';
import type { Dispatch, SetStateAction } from 'react';

export const analysisApi = createApi({
  reducerPath: 'analysisApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['poleData', 'itemData', 'groupItemData', 'analysisData'],
  endpoints: (builder) => ({
    fetchItem: builder.query<Feature, FetchItemArgs>({
      query: ({ item }: FetchItemArgs) => ({
        url: `/v1/analysis${item}`,
        headers: {
          Accept: 'application/geo+json',
        },
      }),
      providesTags: (result) => [
        {
          type: 'poleData' as const,
          id: result?.properties?.level3,
        },
      ],
    }),
    fetchEncroachmentConfig: builder.query<EncroachmentClientConfig[], void>({
      query: () => ({
        url: `/v1/config/encroachment/`,
      }),
    }),
    filterNetworkGroupAnalysis: builder.query<
      ApiResponse<GroupAnalysisData, Summary>,
      FilterNetworkGroupAnalysisArgs
    >({
      query: ({
        analysis_type,
        active_group_id = null,
        active_group_name = null,
        group_id = 1,
        limit = 20,
        page = 1,
        query,
        scope_query,
        plan_id,
      }: FilterNetworkGroupAnalysisArgs) => {
        const useLegacyFilters =
          scope_query === undefined && plan_id === undefined;
        return {
          url:
            useLegacyFilters && active_group_id && active_group_name
              ? `/v1/analysis/group/?active_group_id=${active_group_id}&active_group_name=${encodeURIComponent(
                  active_group_name
                )}`
              : `/v1/analysis/group/`,
          params: {
            group_id: useLegacyFilters
              ? getFilterGroupIdParam(query, group_id)
              : group_id,
            analysis_type,
            limit,
            page,
            scope_query,
            plan_id,
          },
          method: 'POST',
          body: useLegacyFilters ? getFilterQuery(query) : undefined,
        };
      },
      providesTags: ['analysisData'],
    }),
    networkGroupAnalysisPages: builder.infiniteQuery<
      ApiResponse<GroupAnalysisData, Summary>,
      NetworkGroupAnalysisPagesArgs,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          const loaded = allPages.reduce(
            (count, page) => count + page.data.length,
            0
          );
          return loaded < lastPage.summary.total && lastPage.data.length > 0
            ? lastPageParam + 1
            : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: `/v1/analysis/group/`,
        params: {
          group_id: queryArg.group_id,
          analysis_type: queryArg.analysis_type,
          limit: queryArg.limit,
          page: pageParam,
          plan_id: queryArg.plan_id,
        },
        method: 'POST',
      }),
      providesTags: ['analysisData'],
    }),
    filterGroupSummaryAnalysis: builder.query<
      Map<string, GroupSummary>,
      FilterGroupSummaryAnalysisArgs
    >({
      query: ({
        group_id = 1,
        analysis_type,
        query,
        scope_query,
        plan_id,
      }: FilterGroupSummaryAnalysisArgs) => ({
        url: `/v1/analysis/group/summary`,
        params: {
          group_id: group_id,
          analysis_type,
          scope_query,
          plan_id,
        },
        method: 'POST',
        body:
          scope_query === undefined && plan_id === undefined
            ? getFilterQuery(query)
            : undefined,
      }),
      transformResponse: (
        response: GroupSummary[]
      ): Map<string, GroupSummary> => {
        const groupSummaryMap = new Map<string, GroupSummary>();
        if (response !== undefined) {
          response.forEach((i: GroupSummary) => {
            if (i.name != null) groupSummaryMap.set(i.name, i);
          });
        }
        return groupSummaryMap;
      },
    }),
    fetchNetworkGroupItem: builder.query<
      Feature<Geometry, GroupAnalysisSpanData>,
      FetchNetworkGroupItemArgs
    >({
      query: ({ item, group_id }: FetchNetworkGroupItemArgs) => ({
        url: `/v1/analysis/group/${item}`,
        params: {
          group_id,
        },
      }),
      providesTags: (result) => [
        {
          type: 'groupItemData',
          id: result?.properties?.group,
        },
      ],
    }),
  }),
});

type FetchItemArgs = {
  item: string;
};

type FilterNetworkGroupAnalysisArgs = {
  query: FilterState;
  scope_query?: string;
  plan_id?: string;
  analysis_type: GroupAnalysisType;
  active_group_id?: GroupId | null;
  active_group_name?: string | null;
  group_id: GroupId;
  limit?: number;
  page?: number;
};

type NetworkGroupAnalysisPagesArgs = {
  analysis_type: GroupAnalysisType;
  group_id: GroupId;
  limit: number;
  plan_id?: string;
};

type FilterGroupSummaryAnalysisArgs = {
  group_id?: GroupId;
  analysis_type: GroupAnalysisType;
  query: FilterState;
  scope_query?: string;
  plan_id?: string;
};

type FetchNetworkGroupItemArgs = {
  item: string;
  group_id?: GroupId;
};
export interface ListDataProps {
  limit: number;
  page: number;
  isLoading: boolean;
  isFetching: boolean;
  data?: ApiResponse<GroupAnalysisData, Summary>;
  originalArgs?: FilterNetworkGroupAnalysisArgs | void;
  setPage: Dispatch<SetStateAction<number>>;
}

export const resetAnalysisApiState = analysisApi.util.resetApiState;

export const {
  useFetchItemQuery,
  useFetchEncroachmentConfigQuery,
  useFilterNetworkGroupAnalysisQuery,
  useNetworkGroupAnalysisPagesInfiniteQuery,
  useFetchNetworkGroupItemQuery,
  useFilterGroupSummaryAnalysisQuery,
} = analysisApi;
